import React, { createContext, useContext, useReducer } from "react";
import { useData } from "./DataContext";
import { useFiles } from "./FileContext";

// Data management state interface
interface DataManagementState {
  isExporting: boolean;
  isImporting: boolean;
  exportProgress: number;
  importProgress: number;
  error: string | null;
  lastBackup: string | null;
}

// Data management actions
type DataManagementAction =
  | { type: "SET_EXPORTING"; payload: boolean }
  | { type: "SET_IMPORTING"; payload: boolean }
  | { type: "SET_EXPORT_PROGRESS"; payload: number }
  | { type: "SET_IMPORT_PROGRESS"; payload: number }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_LAST_BACKUP"; payload: string };

// Initial state
const initialState: DataManagementState = {
  isExporting: false,
  isImporting: false,
  exportProgress: 0,
  importProgress: 0,
  error: null,
  lastBackup: localStorage.getItem("syndicate-last-backup"),
};

// Data management reducer
function dataManagementReducer(
  state: DataManagementState,
  action: DataManagementAction
): DataManagementState {
  switch (action.type) {
    case "SET_EXPORTING":
      return { ...state, isExporting: action.payload };
    case "SET_IMPORTING":
      return { ...state, isImporting: action.payload };
    case "SET_EXPORT_PROGRESS":
      return { ...state, exportProgress: action.payload };
    case "SET_IMPORT_PROGRESS":
      return { ...state, importProgress: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "SET_LAST_BACKUP":
      return { ...state, lastBackup: action.payload };
    default:
      return state;
  }
}

// Data management context
const DataManagementContext = createContext<{
  state: DataManagementState;
  exportData: (format: "json" | "csv" | "excel") => Promise<void>;
  importData: (file: File) => Promise<void>;
  createBackup: () => Promise<void>;
  restoreBackup: (backupData: any) => Promise<void>;
  clearAllData: () => Promise<void>;
  clearError: () => void;
} | null>(null);

// Data management provider
export function DataManagementProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(dataManagementReducer, initialState);
  const { state: dataState } = useData();
  const { state: fileState } = useFiles();

  // Export data function
  const exportData = async (
    format: "json" | "csv" | "excel"
  ): Promise<void> => {
    dispatch({ type: "SET_EXPORTING", payload: true });
    dispatch({ type: "SET_ERROR", payload: null });

    try {
      // Simulate export progress
      for (let progress = 0; progress <= 100; progress += 10) {
        dispatch({ type: "SET_EXPORT_PROGRESS", payload: progress });
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const exportData = {
        version: "1.0.0",
        exportDate: new Date().toISOString(),
        data: dataState,
        files: fileState.files,
        metadata: {
          totalMembers: dataState.members.length,
          totalFiles: fileState.files.length,
          totalMeetings: dataState.meetings.length,
          totalCases: dataState.cases.length,
          totalFinancialRecords: dataState.financialRecords.length,
        },
      };

      let blob: Blob;
      let filename: string;
      let mimeType: string;

      switch (format) {
        case "json":
          blob = new Blob([JSON.stringify(exportData, null, 2)], {
            type: "application/json",
          });
          filename = `syndicate-data-${
            new Date().toISOString().split("T")[0]
          }.json`;
          mimeType = "application/json";
          break;
        case "csv":
          const csvData = convertToCSV(exportData);
          blob = new Blob([csvData], { type: "text/csv" });
          filename = `syndicate-data-${
            new Date().toISOString().split("T")[0]
          }.csv`;
          mimeType = "text/csv";
          break;
        case "excel":
          // For Excel export, we'll create a JSON file that can be imported into Excel
          blob = new Blob([JSON.stringify(exportData, null, 2)], {
            type: "application/json",
          });
          filename = `syndicate-data-${
            new Date().toISOString().split("T")[0]
          }.xlsx`;
          mimeType =
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
          break;
        default:
          throw new Error("Unsupported export format");
      }

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      dispatch({ type: "SET_EXPORTING", payload: false });
    } catch (error) {
      dispatch({ type: "SET_ERROR", payload: "Failed to export data" });
      dispatch({ type: "SET_EXPORTING", payload: false });
      throw error;
    }
  };

  // Import data function
  const importData = async (file: File): Promise<void> => {
    dispatch({ type: "SET_IMPORTING", payload: true });
    dispatch({ type: "SET_ERROR", payload: null });

    try {
      // Simulate import progress
      for (let progress = 0; progress <= 100; progress += 10) {
        dispatch({ type: "SET_IMPORT_PROGRESS", payload: progress });
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const text = await file.text();
      const importData = JSON.parse(text);

      // Validate import data structure
      if (!importData.data || !importData.version) {
        throw new Error("Invalid data format");
      }

      // Import data (in a real app, this would update the actual data stores)
      localStorage.setItem(
        "syndicate-erp-data",
        JSON.stringify(importData.data)
      );
      localStorage.setItem(
        "syndicate-files",
        JSON.stringify(importData.files || [])
      );

      // Reload the page to apply changes
      window.location.reload();

      dispatch({ type: "SET_IMPORTING", payload: false });
    } catch (error) {
      dispatch({ type: "SET_ERROR", payload: "Failed to import data" });
      dispatch({ type: "SET_IMPORTING", payload: false });
      throw error;
    }
  };

  // Create backup function
  const createBackup = async (): Promise<void> => {
    try {
      const backupData = {
        version: "1.0.0",
        backupDate: new Date().toISOString(),
        data: dataState,
        files: fileState.files,
      };

      const backupKey = `syndicate-backup-${new Date().toISOString()}`;
      localStorage.setItem(backupKey, JSON.stringify(backupData));
      localStorage.setItem("syndicate-last-backup", new Date().toISOString());

      dispatch({ type: "SET_LAST_BACKUP", payload: new Date().toISOString() });
    } catch (error) {
      dispatch({ type: "SET_ERROR", payload: "Failed to create backup" });
      throw error;
    }
  };

  // Restore backup function
  const restoreBackup = async (backupData: any): Promise<void> => {
    try {
      localStorage.setItem(
        "syndicate-erp-data",
        JSON.stringify(backupData.data)
      );
      localStorage.setItem(
        "syndicate-files",
        JSON.stringify(backupData.files || [])
      );

      // Reload the page to apply changes
      window.location.reload();
    } catch (error) {
      dispatch({ type: "SET_ERROR", payload: "Failed to restore backup" });
      throw error;
    }
  };

  // Clear all data function
  const clearAllData = async (): Promise<void> => {
    try {
      localStorage.removeItem("syndicate-erp-data");
      localStorage.removeItem("syndicate-files");
      localStorage.removeItem("syndicate-auth-user");

      // Reload the page to apply changes
      window.location.reload();
    } catch (error) {
      dispatch({ type: "SET_ERROR", payload: "Failed to clear data" });
      throw error;
    }
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: "SET_ERROR", payload: null });
  };

  // Convert data to CSV format
  const convertToCSV = (data: any): string => {
    const csvRows: string[] = [];

    // Add metadata
    csvRows.push("Data Type,Count");
    csvRows.push(`Members,${data.metadata.totalMembers}`);
    csvRows.push(`Files,${data.metadata.totalFiles}`);
    csvRows.push(`Meetings,${data.metadata.totalMeetings}`);
    csvRows.push(`Cases,${data.metadata.totalCases}`);
    csvRows.push(`Financial Records,${data.metadata.totalFinancialRecords}`);
    csvRows.push("");

    // Add members data
    if (data.data.members && data.data.members.length > 0) {
      csvRows.push("MEMBERS");
      csvRows.push(
        "ID,Name,Email,Phone,License Number,Status,Type,Join Date,Renewal Date"
      );
      data.data.members.forEach((member: any) => {
        csvRows.push(
          `${member.id},${member.name},${member.email},${member.phone},${member.licenseNumber},${member.licenseStatus},${member.membershipType},${member.joinDate},${member.renewalDate}`
        );
      });
      csvRows.push("");
    }

    // Add financial records
    if (data.data.financialRecords && data.data.financialRecords.length > 0) {
      csvRows.push("FINANCIAL RECORDS");
      csvRows.push("ID,Type,Category,Amount,Date,Description,Department");
      data.data.financialRecords.forEach((record: any) => {
        csvRows.push(
          `${record.id},${record.type},${record.category},${record.amount},${
            record.date
          },${record.description},${record.department || ""}`
        );
      });
      csvRows.push("");
    }

    return csvRows.join("\n");
  };

  return (
    <DataManagementContext.Provider
      value={{
        state,
        exportData,
        importData,
        createBackup,
        restoreBackup,
        clearAllData,
        clearError,
      }}
    >
      {children}
    </DataManagementContext.Provider>
  );
}

// Data management hook
export function useDataManagement() {
  const context = useContext(DataManagementContext);
  if (!context) {
    throw new Error(
      "useDataManagement must be used within a DataManagementProvider"
    );
  }
  return context;
}
